'use client';

import { useEffect, type RefObject } from 'react';

export const HEADER_VISIBLE_HEIGHT_VAR = '--hen-site-header-visible-height';
export const HEADER_TRANSITION_DURATION_VAR =
  '--hen-site-header-transition-duration';

/** Upward scroll distance (px) needed before the header slides back in. */
export const REVEAL_THRESHOLD_PX = 8;

const REVEAL_DURATION = 'var(--duration-normal, 200ms)';
const NO_TRANSITION = '0ms';

/** Time an anchor click waits for its jump to start scrolling. */
const ANCHOR_JUMP_START_MS = 1200;

/**
 * Scroll idle time that ends an anchor jump. Long smooth jumps outlast any
 * fixed timeout, so the lock is extended on every scroll frame. `scrollend`
 * is not used: Chromium fires one for the interrupted prior scroll right
 * after the click, before the jump itself starts.
 */
const ANCHOR_JUMP_IDLE_MS = 200;

type HeaderState = 'shown' | 'partial' | 'hidden';

/**
 * "Headroom" behavior for a sticky site header.
 *
 * Scrolling down moves the header off screen 1:1 with the page, as if it
 * were not sticky. Scrolling up past `REVEAL_THRESHOLD_PX` slides it back
 * in. Near the top of the page the header always follows the natural
 * layout.
 *
 * The header is moved with direct DOM writes (not React state) so a scroll
 * frame never re-renders the navbar subtree. The visible height and the
 * current transition duration are published as CSS variables on `<body>`
 * so sibling sticky elements (the TOC bar) can dock under the header and
 * animate in step with it. `<body>` rather than `<html>` so writers that
 * observe `documentElement.style` are not woken on every frame.
 *
 * The header stays visible while a disclosure inside it is expanded or a
 * control inside it has keyboard focus. In-page anchor jumps keep it
 * hidden until the jump ends, so the target heading is not covered by a
 * header that reappears mid-jump.
 */
export function useHideOnScroll(headerRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const bodyStyle = document.body.style;
    const reducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let headerHeight = header.getBoundingClientRect().height;
    let lastY = Math.max(0, window.scrollY);
    let hiddenOffset = Math.min(lastY, headerHeight);
    let upDistance = 0;
    let anchorJumpActive = false;
    let anchorJumpTimer: ReturnType<typeof setTimeout> | undefined;
    let ticking = false;
    let frame = 0;

    const hasExpandedDisclosure = () =>
      header.querySelector('[aria-expanded="true"]') !== null;
    const isFocusVisible = (element: Element) => {
      try {
        return element.matches(':focus-visible');
      } catch {
        return false;
      }
    };

    // Tracked from mutation and focus events so scroll frames never query
    // the DOM or force a style recalculation.
    let expandedInside = hasExpandedDisclosure();
    let focusVisibleInside =
      document.activeElement instanceof Element &&
      header.contains(document.activeElement) &&
      isFocusVisible(document.activeElement);

    const mustStayVisible = () => expandedInside || focusVisibleInside;

    // Writing a custom property on `<body>` restyles the whole page, so
    // frames that change nothing (e.g. scrolling while fully hidden) skip it.
    let appliedKey = '';

    const apply = (animate: boolean) => {
      const duration =
        animate && !reducedMotion ? REVEAL_DURATION : NO_TRANSITION;
      const key = `${hiddenOffset}|${headerHeight}|${duration}`;
      if (key === appliedKey) return;
      appliedKey = key;
      const state: HeaderState =
        hiddenOffset <= 0
          ? 'shown'
          : hiddenOffset >= headerHeight
          ? 'hidden'
          : 'partial';
      bodyStyle.setProperty(HEADER_TRANSITION_DURATION_VAR, duration);
      bodyStyle.setProperty(
        HEADER_VISIBLE_HEIGHT_VAR,
        `${headerHeight - hiddenOffset}px`,
      );
      header.style.transform = `translateY(${-hiddenOffset}px)`;
      header.setAttribute('data-header-state', state);
    };

    const update = () => {
      ticking = false;
      const y = Math.max(0, window.scrollY);
      const delta = y - lastY;
      lastY = y;

      if (mustStayVisible()) {
        upDistance = 0;
        const wasHidden = hiddenOffset > 0;
        hiddenOffset = 0;
        apply(wasHidden);
        return;
      }

      if (delta > 0) {
        upDistance = 0;
        hiddenOffset = Math.min(hiddenOffset + delta, headerHeight);
        apply(false);
        return;
      }

      if (delta < 0) {
        if (y < hiddenOffset) {
          hiddenOffset = y;
          apply(false);
          return;
        }
        if (anchorJumpActive || hiddenOffset === 0) return;
        upDistance -= delta;
        if (upDistance > REVEAL_THRESHOLD_PX) {
          upDistance = 0;
          hiddenOffset = 0;
          apply(true);
        }
      }
    };

    const clearAnchorJumpTimer = () => {
      if (anchorJumpTimer !== undefined) {
        clearTimeout(anchorJumpTimer);
        anchorJumpTimer = undefined;
      }
    };

    const releaseAnchorJump = () => {
      anchorJumpActive = false;
      clearAnchorJumpTimer();
    };

    const armAnchorJumpTimer = (ms: number) => {
      clearAnchorJumpTimer();
      anchorJumpTimer = setTimeout(releaseAnchorJump, ms);
    };

    const onScroll = () => {
      if (anchorJumpActive) armAnchorJumpTimer(ANCHOR_JUMP_IDLE_MS);
      if (ticking) return;
      ticking = true;
      frame = window.requestAnimationFrame(update);
    };

    const onClick = (event: MouseEvent) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest<HTMLAnchorElement>('a[href^="#"]');
      if (!anchor || anchor.getAttribute('href') === '#') return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;
      if (mustStayVisible()) return;
      anchorJumpActive = true;
      upDistance = 0;
      armAnchorJumpTimer(ANCHOR_JUMP_START_MS);
      // `scroll-padding-top` only reserves room for the TOC bar, so the
      // header must be out of the way before the jump lands.
      if (hiddenOffset < headerHeight) {
        hiddenOffset = headerHeight;
        apply(true);
      }
    };

    const revealIfRequired = () => {
      if (hiddenOffset === 0 || !mustStayVisible()) return;
      upDistance = 0;
      hiddenOffset = 0;
      apply(true);
    };

    const onFocusIn = (event: FocusEvent) => {
      focusVisibleInside =
        event.target instanceof Element && isFocusVisible(event.target);
      revealIfRequired();
    };

    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget;
      if (next instanceof Node && header.contains(next)) return;
      focusVisibleInside = false;
    };

    const expandedObserver =
      typeof MutationObserver === 'undefined'
        ? undefined
        : new MutationObserver(() => {
            expandedInside = hasExpandedDisclosure();
            revealIfRequired();
          });
    expandedObserver?.observe(header, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded'],
    });

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(() => {
            headerHeight = header.getBoundingClientRect().height;
            hiddenOffset = Math.min(hiddenOffset, headerHeight);
            apply(false);
          });
    resizeObserver?.observe(header);

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('click', onClick, true);
    header.addEventListener('focusin', onFocusIn);
    header.addEventListener('focusout', onFocusOut);
    apply(false);

    return () => {
      resizeObserver?.disconnect();
      expandedObserver?.disconnect();
      header.removeEventListener('focusin', onFocusIn);
      header.removeEventListener('focusout', onFocusOut);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('click', onClick, true);
      if (ticking) window.cancelAnimationFrame(frame);
      releaseAnchorJump();
      bodyStyle.removeProperty(HEADER_VISIBLE_HEIGHT_VAR);
      bodyStyle.removeProperty(HEADER_TRANSITION_DURATION_VAR);
      header.style.removeProperty('transform');
      header.removeAttribute('data-header-state');
    };
  }, [headerRef]);
}
